/**
 * LayoutPageSetup — extract paper-sheet dimensions and plot settings from a
 * DXF LAYOUT entity (AutoCAD OBJECTS-section LAYOUT object with embedded
 * PLOTSETTINGS groups 4/72/46/47/10/20/11/21).
 *
 * AutoCAD stores page setup information directly on the LAYOUT entity:
 *   group 4  → paper size name ("ISO A4 (297.00 x 210.00 MM)", "A4", …)
 *   group 72 → plot paper units (0 = inches, 1 = mm, 2 = pixels)
 *   group 10/20 → minimum limits (lower-left corner, often 0,0 or slightly
 *                 negative when the plot margin is non-zero)
 *   group 11/21 → maximum limits (upper-right corner = full paper extent)
 *
 * Our drawing-model Layout type already captures all four fields.  This module
 * converts them into a typed LayoutPageSetup struct that the renderer can
 * consume without knowing the DXF group-code details.
 *
 * The "margins" field is approximated from minimumLimits: if AutoCAD placed the
 * lower-left corner at (-d, -d) instead of (0, 0), the effective margin is |d|
 * on those sides.  The real plot-margin group codes (40–43) are not yet captured
 * in the drawing-model Layout schema, so this is the best available proxy.
 */

import type { Layout } from "./model";

/**
 * Sheet size in the layout's native world-space units.
 * `widthMm`/`heightMm` are in mm for metric drawings (plotPaperUnits=1) and
 * in inches for imperial drawings (plotPaperUnits=0).  The sibling `units`
 * field on `LayoutPageSetup` identifies which system applies.
 * Consumer code (PaperSpaceLayer, camera autofit) uses these values directly
 * as world-space coordinates — no further conversion is applied.
 */
export interface LayoutSheetSize {
  widthMm: number;
  heightMm: number;
}

/** Print margins in the layout's native world-space units (mm or inches, matching LayoutSheetSize). */
export interface LayoutMargins {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface LayoutPageSetup {
  sheetSize: LayoutSheetSize;
  /** Primary unit system derived from plotPaperUnits (0=inches, 1=mm, 2=pixels). */
  units: "mm" | "inches" | "pixels";
  margins: LayoutMargins;
  /**
   * Combined print-scale factor (numerator / denominator).
   * AutoCAD LAYOUT group codes 148 (numerator) / 149 (denominator) are not
   * yet captured in the drawing-model; we default to 1:1.
   */
  printScale: number;
  printScaleNumerator: number;
  printScaleDenominator: number;
  /** Raw paper-size name from DXF group 4 (e.g. "ISO A4 (297.00 x 210.00 MM)"). */
  paperName: string;
  orientation: "portrait" | "landscape";
  /**
   * World-space position of the paper lower-left corner (= minimumLimits).
   * When minimumLimits is non-zero (common in templates), paper-space
   * entities live at this offset in world space.  PaperSpaceLayer and
   * camera auto-fit must apply this offset so the sheet aligns with entities.
   */
  origin: { x: number; y: number };
}


/**
 * Extract a LayoutPageSetup from a Layout entity.
 *
 * Returns `null` when the layout carries no usable page-size data (both
 * limits are zero — this happens when the LAYOUT object was generated
 * without an explicit PLOTSETTINGS block, i.e. "no PAGESETUP defined").
 */
export function extractPageSetup(
  layout: Layout | null | undefined,
): LayoutPageSetup | null {
  if (!layout) return null;

  const { minimumLimits, maximumLimits, plotPaperUnits, paperSize } = layout;

  // Raw sheet dimensions in DXF units (mm when plotPaperUnits=1, inches when 0).
  const rawWidth = maximumLimits.x - minimumLimits.x;
  const rawHeight = maximumLimits.y - minimumLimits.y;

  // Degenerate — any non-positive dimension produces a zero-sized or
  // collapsed sheet which causes divide-by-zero in auto-fit.
  if (rawWidth <= 0 || rawHeight <= 0) return null;

  // plotPaperUnits: 0 = inches, 1 = mm, 2 = pixels
  const units: "mm" | "inches" | "pixels" =
    plotPaperUnits === 0 ? "inches" : plotPaperUnits === 2 ? "pixels" : "mm";

  // Store native world-space dimensions — no inches→mm conversion.
  // PaperSpaceLayer and camera autofit use these as world coordinates
  // which already match the paper-space geometry (11×8.5 for Letter in
  // inches, 297×210 for A4 in mm).  The `units` field tells consumers
  // which system applies.
  const widthMm = Math.max(0, rawWidth);
  const heightMm = Math.max(0, rawHeight);

  // The lower-left corner of the paper sheet in world space equals
  // minimumLimits (group codes 10/20).  When non-zero (common in DXF
  // templates), paper-space entities are authored relative to this offset,
  // so consumers must translate the paper sheet accordingly.
  const origin = { x: minimumLimits.x, y: minimumLimits.y };

  // Margin approximation: only the portion of minimumLimits that lies
  // below the origin is a printable-area inset (negative = margin, positive
  // = the paper starts away from world origin — not a margin).
  const leftRaw = minimumLimits.x < 0 ? Math.abs(minimumLimits.x) : 0;
  const bottomRaw = minimumLimits.y < 0 ? Math.abs(minimumLimits.y) : 0;
  const margins: LayoutMargins = {
    left: leftRaw,
    bottom: bottomRaw,
    // Right/top margins are not separately stored in the current schema;
    // mirror the left/bottom values as a symmetric approximation.
    right: leftRaw,
    top: bottomRaw,
  };

  return {
    sheetSize: { widthMm, heightMm },
    units,
    margins,
    origin,
    printScale: 1,
    printScaleNumerator: 1,
    printScaleDenominator: 1,
    paperName: paperSize,
    orientation: widthMm >= heightMm ? "landscape" : "portrait",
  };
}

/** A4 landscape, 10 mm margins — used when no PAGESETUP is defined. */
export function defaultPageSetup(): LayoutPageSetup {
  return {
    sheetSize: { widthMm: 297, heightMm: 210 },
    units: "mm",
    margins: { left: 10, right: 10, top: 10, bottom: 10 },
    origin: { x: 0, y: 0 },
    printScale: 1,
    printScaleNumerator: 1,
    printScaleDenominator: 1,
    paperName: "A4",
    orientation: "landscape",
  };
}
